import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../firebase';
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, query, where, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../../styles.css';

const AdminPanel = () => {
  const { isAdmin } = useAuth();
  const [sectionName, setSectionName] = useState('');
  const [sections, setSections] = useState([]); // {slug, label}
  const [selectedSection, setSelectedSection] = useState(''); // slug
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [preview, setPreview] = useState('');
  const [sectionImages, setSectionImages] = useState([]); // images in selected section
  const [storeItemsSrcSet, setStoreItemsSrcSet] = useState(new Set());
  const [hiddenUrl, setHiddenUrl] = useState('');
  const [hiddenList, setHiddenList] = useState([]);
  const [storeDocs, setStoreDocs] = useState([]);

  const slugify = (str) => str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'gallerySections'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const mapped = data.map(s => ({ id: s.id, slug: s.slug || slugify(s.name || s.label || ''), label: s.label || s.name || '', createdAt: s.createdAt || null }))
        .filter(s => s.slug);
      setSections(mapped);
    });
    return () => unsub();
  }, []);

  // Watch images in selected section
  useEffect(() => {
    if (!selectedSection) { setSectionImages([]); return undefined; }
    const unsub = onSnapshot(query(collection(db, 'galleryImages'), where('section', '==', selectedSection)), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSectionImages(data);
    }, () => setSectionImages([]));
    return () => unsub();
  }, [selectedSection]);

  // Watch store items to know which images are included
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'storeItems'), (snap) => {
      const srcs = new Set();
      const docs = [];
      snap.forEach(d => { const s = d.data().src; if (s) srcs.add(s); docs.push({ id: d.id, ...d.data() }); });
      setStoreItemsSrcSet(srcs);
      setStoreDocs(docs);
    });
    return () => unsub();
  }, []);

  // Watch hidden images list
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'hiddenImages'), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      setHiddenList(list);
    });
    return () => unsub();
  }, []);

  if (!isAdmin) {
    return (
      <section className="section contact-section">
        <h2>Admin</h2>
        <p>You do not have access to this page.</p>
      </section>
    );
  }

  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!sectionName.trim()) return;
    setStatus('Creating section...');
    const label = sectionName.trim();
    const slug = slugify(label);
    // Store both slug and name for backward compatibility
    await addDoc(collection(db, 'gallerySections'), { slug, label, name: slug, createdAt: serverTimestamp() });
    setSectionName('');
    setStatus('Section created. It will appear in Gallery and menu shortly.');
  };

  const handleUploadImage = async (e) => {
    e.preventDefault();
    if (!selectedSection || !file) { setStatus('Choose a section and an image.'); return; }
    // Basic validation
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setStatus('Unsupported file type. Please use JPG, PNG, or WEBP.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) { // 15MB
      setStatus('File too large. Max 15 MB.');
      return;
    }
    setStatus('Uploading image...');
    const storageRef = ref(storage, `gallery/${selectedSection}/${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    await addDoc(collection(db, 'galleryImages'), { section: selectedSection, src: url, description, createdAt: serverTimestamp() });
    setFile(null);
    setDescription('');
    setStatus('Image uploaded.');
    setPreview('');
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f) {
      try {
        const url = URL.createObjectURL(f);
        setPreview(url);
      } catch { setPreview(''); }
    } else {
      setPreview('');
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      // Find src for this image
      const img = sectionImages.find(i => i.id === imageId);
      await deleteDoc(doc(db, 'galleryImages', imageId));
      // Also remove from store by src
      if (img?.src) {
        const snap = await getDocs(query(collection(db, 'storeItems'), where('src', '==', img.src)));
        await Promise.all(snap.docs.map(d => deleteDoc(doc(db, 'storeItems', d.id))));
      }
      setStatus('Image deleted.');
    } catch { setStatus('Failed to delete image.'); }
  };

  const handleAddToStore = async (image) => {
    try {
      await addDoc(collection(db, 'storeItems'), { src: image.src, description: image.description || '', section: image.section || selectedSection, imageId: image.id || null, createdAt: serverTimestamp() });
      setStatus('Added to store.');
    } catch { setStatus('Failed to add to store.'); }
  };

  const handleRemoveFromStore = async (image) => {
    try {
      const snap = await getDocs(query(collection(db, 'storeItems'), where('src', '==', image.src)));
      await Promise.all(snap.docs.map(d => deleteDoc(doc(db, 'storeItems', d.id))));
      setStatus('Removed from store.');
    } catch { setStatus('Failed to remove from store.'); }
  };

  const handleDeleteSection = async (slug, label) => {
    try {
      const imgs = await getDocs(query(collection(db, 'galleryImages'), where('section', '==', slug)));
      if (!imgs.empty) {
        setStatus('Section has images. Delete images first.');
        return;
      }
      // Try delete by slug; if nothing found, try legacy 'name'
      let secs = await getDocs(query(collection(db, 'gallerySections'), where('slug', '==', slug)));
      if (secs.empty) {
        secs = await getDocs(query(collection(db, 'gallerySections'), where('name', '==', slug)));
      }
      if (secs.empty && label) {
        secs = await getDocs(query(collection(db, 'gallerySections'), where('label', '==', label)));
      }
      if (secs.empty) {
        setStatus('No section document found to delete.');
        return;
      }
      await Promise.all(secs.docs.map(d => deleteDoc(doc(db, 'gallerySections', d.id))));
      setStatus('Section deleted.');
    } catch { setStatus('Failed to delete section.'); }
  };

  return (
    <section className="section contact-section">
      <h2>Admin Panel</h2>
      <div className="contact-form" style={{ gap: 16 }}>
        <form onSubmit={handleCreateSection}>
          <div className="form-group">
            <label htmlFor="sectionName">New Section Name</label>
            <input id="sectionName" value={sectionName} onChange={(e) => setSectionName(e.target.value)} placeholder="e.g. Sculpture" />
          </div>
          <button type="submit" className="btn-submit">Create Section</button>
        </form>

        <form onSubmit={handleUploadImage}>
          <div className="form-group">
            <label htmlFor="section">Section</label>
            <select id="section" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} required>
              <option value="">Select section</option>
              {sections.map(s => (
                <option key={s.slug} value={s.slug}>{s.label}</option>
              ))}
              {/* defaults to ensure baseline availability */}
              <option value="landscape">Landscape</option>
              <option value="abstract">Abstract</option>
              <option value="realms">Realms</option>
              <option value="patterns">Patterns</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="image">Image</label>
            <input id="image" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} required />
            {preview && (
              <div style={{ marginTop: 8 }}>
                <img src={preview} alt="Preview" style={{ maxWidth: 220, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="desc">Description</label>
            <input id="desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Acrylic on canvas..." />
          </div>
          <button type="submit" className="btn-submit">Upload Image</button>
        </form>
        {status && <p className={status.includes('failed') ? 'error-message' : 'success-message'}>{status}</p>}
      </div>

      {/* Management: Current Section Images */}
      <div className="contact-form" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Manage Images in Section {selectedSection || ''}</h3>
        <div className="image-grid">
          {sectionImages.map((img) => (
            <div key={img.id} className="image-item">
              <img src={img.src} alt={img.description || 'Artwork'} className="gallery-image" />
              <p className="image-description">{img.description || ''}</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {storeItemsSrcSet.has(img.src) ? (
                  <button type="button" className="btn-outline" onClick={() => handleRemoveFromStore(img)}>Remove from Store</button>
                ) : (
                  <button type="button" className="btn-outline" onClick={() => handleAddToStore(img)}>Add to Store</button>
                )}
                <button type="button" className="btn-outline" onClick={() => handleDeleteImage(img.id)}>Delete Image</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Management: Sections */}
      <div className="contact-form" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Manage Sections</h3>
        <div style={{ display: 'grid', gap: 8 }}>
          {sections
            .slice()
            .sort((a, b) => {
              const ta = a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
              const tb = b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
              return tb - ta; // newest first
            })
            .map((s) => (
            <div key={s.slug} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                defaultValue={s.label}
                onBlur={async (e) => {
                  try {
                    await setDoc(doc(db, 'gallerySections', s.id), { label: e.target.value }, { merge: true });
                    setStatus('Section name updated.');
                  } catch { setStatus('Failed to update name.'); }
                }}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <button type="button" className="btn-outline" onClick={() => handleDeleteSection(s.slug, s.label)}>Delete</button>
            </div>
          ))}
        </div>
      </div>

      {/* Management: Store Items (delete) */}
      <div className="contact-form" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Manage Store Items</h3>
        <div className="image-grid">
          {storeDocs.map((it) => (
            <div key={it.id} className="image-item">
              <img src={it.src} alt={it.description || 'Store item'} className="gallery-image" />
              <p className="image-description">{it.description || ''}</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button type="button" className="btn-outline" onClick={async () => {
                  try { await deleteDoc(doc(db, 'storeItems', it.id)); setStatus('Removed from store.'); } catch { setStatus('Failed to remove.'); }
                }}>Delete from Store</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Management: Hide/Unhide Legacy Images by URL */}
      <div className="contact-form" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Hide/Unhide Legacy Images (static file paths)</h3>
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (!hiddenUrl.trim()) return;
          try {
            await addDoc(collection(db, 'hiddenImages'), { src: hiddenUrl.trim() });
            setHiddenUrl('');
            setStatus('Image hidden.');
          } catch { setStatus('Failed to hide image.'); }
        }}>
          <div className="form-group">
            <label htmlFor="hideurl">Static image URL (e.g., /TzofitPaintings/...)</label>
            <input id="hideurl" value={hiddenUrl} onChange={(e) => setHiddenUrl(e.target.value)} placeholder="/TzofitPaintings/..." />
          </div>
          <button type="submit" className="btn-submit">Hide</button>
        </form>
        <div style={{ marginTop: 8 }}>
          <h4>Hidden list</h4>
          <ul>
            {hiddenList.map(h => (
              <li key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ wordBreak: 'break-all' }}>{h.src}</span>
                <button type="button" className="btn-outline" onClick={async () => {
                  try { await deleteDoc(doc(db, 'hiddenImages', h.id)); setStatus('Unhidden.'); } catch { setStatus('Failed to unhide.'); }
                }}>Unhide</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default AdminPanel;


